class SizeToggleDropdown {
  constructor(element) {
    if (!element) {
      console.warn('SizeToggleDropdown: element parameter is required');
      return;
    }

    this.element = element;
    this.isExpanded = false;

    this.ui = {
      dropdownToggle: element.querySelector('.custom-dropdown-toggle'),
      dropdownMenu: element.querySelector('.custom-dropdown-menu'),
      dropdownItems: element.querySelectorAll('.custom-dropdown-item'),
      selectedSizeText: element.querySelector('.selected-size-text'),
      addToCartButton: document.querySelector('.product-form__submit'),
    };

    this.selectedVariantId = null;
    this.selectedSizeText = 'Select a size';

    this.init();
  }

  init() {
    console.log('SizeToggleDropdown initialized');
    if (!this.validateElements()) {
      console.warn('SizeToggleDropdown initialization failed - missing required elements');
      return;
    }
    this.setupDropdownToggle();
    this.bindEvents();
  }

  validateElements() {
    if (!this.ui.dropdownToggle) {
      console.error('Dropdown toggle not found');
      return false;
    }
    if (!this.ui.dropdownMenu) {
      console.error('Dropdown menu not found');
      return false;
    }
    console.log('All required elements found');
    return true;
  }

  setupDropdownToggle() {
    if (this.ui.addToCartButton) {
      // Ensure the toggle button is clickable
      this.ui.addToCartButton.disabled = false;
      this.ui.addToCartButton.removeAttribute('disabled');
      this.ui.addToCartButton.style.pointerEvents = 'auto';
      this.ui.addToCartButton.style.cursor = 'pointer';

      // Add ARIA attributes for accessibility
      this.ui.addToCartButton.setAttribute('aria-haspopup', 'listbox');
      this.ui.addToCartButton.setAttribute('aria-expanded', 'false');
      console.log('Dropdown toggle setup complete');
    }
  }

  bindEvents() {
    // Toggle dropdown on button click
    if (this.ui.addToCartButton) {
      this.ui.addToCartButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.selectedVariantId) {
          // No size selected yet, just toggle the dropdown
          this.toggleDropdown();
        } else {
          // Size selected, proceed to add to cart
          this.handleAddToCart(e);
        }
      });
      if (this.ui.dropdownToggle) {
        this.ui.dropdownToggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleDropdown();
        });
      }
    }

    // Handle size selection
    if (this.ui.dropdownItems) {
      this.ui.dropdownItems.forEach((item, index) => {
        if (item) {
          item.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.handleSizeSelection(item, index);
          });
        }
      });
    }

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      this.handleBodyClick(e);
    });

    // Close dropdown with Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isExpanded) {
        this.setExpanded(false);
      }
    });

    // Add to Cart functionality is already handled above in the toggle button logic

    // Handle window resize
    window.addEventListener('resize', () => {
      if (this.isExpanded) {
        this.setExpanded(false);
      }
    });
  }

  toggleDropdown() {
    console.log('Toggle dropdown clicked');
    this.setExpanded(!this.isExpanded);
  }

  setExpanded(expanded) {
    this.isExpanded = expanded;
    const action = expanded ? 'remove' : 'add';

    if (this.ui.dropdownMenu) {
      this.ui.dropdownMenu.classList[action]('hidden');
    }

    if (this.ui.dropdownToggle) {
      this.ui.dropdownToggle.setAttribute('aria-expanded', expanded.toString());
    }

    // Add/remove body class to prevent scrolling when dropdown is open
    document.body.classList[expanded ? 'add' : 'remove']('size-dropdown-expanded');

    console.log(`Dropdown ${expanded ? 'opened' : 'closed'}`);
  }

  handleBodyClick(event) {
    // Close dropdown if clicking outside
    if (
      this.isExpanded &&
      event &&
      event.target &&
      this.element &&
      !this.element.contains(event.target) &&
      !event.target.closest('.custom-dropdown-toggle') &&
      !event.target.closest('.custom-dropdown-menu')
    ) {
      this.setExpanded(false);
      console.log('Dropdown closed by outside click');
    }
  }

  handleSizeSelection(item, index) {
    // Check if item is disabled or out of stock
    if (item.classList.contains('disabled') || item.classList.contains('opacity-50')) {
      console.log('Size is disabled/out of stock, ignoring click');
      return;
    }

    const variantId = item.dataset.value;
    const sizeText = item.textContent.trim().split(' - ')[0]; // Remove "Out of stock" text

    console.log('Size selected:', sizeText, 'Variant ID:', variantId);

    // Update selection state
    this.updateSelection(item, sizeText, variantId);

    // Close dropdown
    this.setExpanded(false);

    // Enable add to cart
    this.enableAddToCart(variantId);
  }

  updateSelection(selectedItem, sizeText, variantId) {
    // Remove previous selection
    if (this.ui.dropdownItems) {
      this.ui.dropdownItems.forEach((item) => {
        if (item) {
          item.classList.remove('bg-black', 'text-white', 'selected');
        }
      });
    }

    // Add selection to current item
    selectedItem.classList.add('bg-black', 'text-white', 'selected');

    // Update button text
    if (this.ui.selectedSizeText) {
      this.ui.selectedSizeText.textContent = sizeText;
    }

    // Store selected values
    this.selectedVariantId = variantId;
    this.selectedSizeText = sizeText;

    console.log('Selection updated:', { sizeText, variantId });
  }

  enableAddToCart(variantId) {
    if (this.ui.addToCartButton) {
      this.ui.addToCartButton.disabled = false;
      this.ui.addToCartButton.dataset.variantId = variantId;
      this.ui.addToCartButton.textContent = 'Add to Cart';
      this.ui.addToCartButton.classList.remove('disabled');

      console.log('Add to cart enabled with variant:', variantId);
    }
  }

  handleAddToCart(event) {
    const variantId = this.selectedVariantId || this.ui.addToCartButton.dataset.variantId;

    if (!variantId) {
      console.warn('No variant selected - please select a size first');
      this.showSizeRequiredMessage();
      return;
    }

    console.log('Adding to cart with variant ID:', variantId);
    this.setAddToCartState('loading');

    this.addToCart(variantId)
      .then(() => {
        this.setAddToCartState('success');
        this.updateCartDrawer();
      })
      .catch((error) => {
        console.error('Add to cart error:', error);
        this.setAddToCartState('error');
      });
  }

  setAddToCartState(state) {
    if (!this.ui.addToCartButton) return;

    const states = {
      loading: { text: 'Adding...', disabled: true },
      success: { text: 'Added!', disabled: true },
      error: { text: 'Error', disabled: true },
      default: { text: 'Add to Cart', disabled: false },
    };

    const currentState = states[state] || states.default;
    this.ui.addToCartButton.textContent = currentState.text;
    this.ui.addToCartButton.disabled = currentState.disabled;

    if (state === 'success' || state === 'error') {
      setTimeout(
        () => {
          this.setAddToCartState('default');
        },
        state === 'success' ? 1500 : 2000,
      );
    }
  }

  showSizeRequiredMessage() {
    // Briefly highlight the size selector to indicate selection is required
    if (this.ui.dropdownToggle) {
      this.ui.dropdownToggle.style.borderColor = '#ef4444';
      setTimeout(() => {
        this.ui.dropdownToggle.style.borderColor = '';
      }, 2000);
    }
  }

  async addToCart(variantId) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId), quantity: 1 }],
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to add to cart');
    }

    return response.json();
  }

  async updateCartDrawer() {
    try {
      const response = await fetch('/cart.js');
      const cartData = await response.json();

      const sectionsResponse = await fetch('/?section_id=cart-drawer');
      const sectionsHtml = await sectionsResponse.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionsHtml, 'text/html');
      const newSection = doc.querySelector('.shopify-section');

      const currentDrawer = document.querySelector('cart-drawer');

      if (currentDrawer && newSection && typeof currentDrawer.renderContents === 'function') {
        // Use the proper renderContents method instead of innerHTML replacement
        const parsedState = {
          sections: {
            'cart-drawer': sectionsHtml,
          },
          item_count: cartData.item_count,
          items: cartData.items,
        };
        currentDrawer.renderContents(parsedState);
      } else if (currentDrawer && typeof currentDrawer.open === 'function') {
        // Fallback: just open the existing drawer
        currentDrawer.open();
      }
    } catch (error) {
      console.warn('Could not update cart drawer:', error);
    }
  }

  // Static method to initialize all size dropdowns on the page
  static init() {
    const dropdownElements = document.querySelectorAll('.product-options-wrapper');
    dropdownElements.forEach((element) => {
      if (element) {
        try {
          new SizeToggleDropdown(element);
        } catch (error) {
          console.warn('Failed to initialize SizeToggleDropdown:', error);
        }
      }
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function () {
  console.log('Initializing Size Toggle Dropdowns');
  SizeToggleDropdown.init();
});

// Export for potential external use
window.SizeToggleDropdown = SizeToggleDropdown;
